import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  MultiSelect,
  RelativeDateInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@tellescope/ui";
import type { DynamicDateValue, RelativeDateValue } from "@tellescope/ui";
import { Trash2 } from "lucide-react";
import type { ItemCondition, DataSourceType } from "../../../shared/schema/visibility";
import { isComparisonOperator } from "../../../shared/schema/visibility";
import {
  getFieldDefinition,
  getFieldsForSource,
  getOperatorsForField,
  getBaseFieldId,
  getSuffixKey,
  createFieldIdWithSuffix,
} from "./constants";

function createDefaultRelativeDateValue(): RelativeDateValue {
  return { $relative: { amount: 30, unit: "days", direction: "ago" } };
}

function getOperatorFromValue(value: ItemCondition["value"]): string {
  if (value === null || typeof value !== "object") return "$eq";
  const keys = Object.keys(value);
  return keys[0] ?? "$eq";
}

function getValueFromCondition(value: ItemCondition["value"]): string | number | boolean | null | DynamicDateValue {
  if (value === null || typeof value !== "object") return value;
  if (!isComparisonOperator(value)) return "";
  if ("$eq" in value) return value.$eq;
  if ("$ne" in value) return value.$ne;
  if ("$gt" in value) return value.$gt;
  if ("$gte" in value) return value.$gte;
  if ("$lt" in value) return value.$lt;
  if ("$lte" in value) return value.$lte;
  if ("$exists" in value) return value.$exists;
  if ("$regex" in value) return value.$regex;
  if ("$in" in value) return value.$in.join(", ");
  if ("$nin" in value) return value.$nin.join(", ");
  return "";
}

function isDynamicDateValue(value: unknown): value is DynamicDateValue {
  if (value === "$now" || value === "$today") return true;
  if (typeof value === "object" && value !== null && "$relative" in value) return true;
  return false;
}

function extractDateValue(value: ItemCondition["value"]): DynamicDateValue | number | null {
  const extracted = getValueFromCondition(value);
  if (isDynamicDateValue(extracted)) return extracted;
  if (typeof extracted === "number") return extracted;
  return null;
}

function createValueWithOperator(
  operator: string,
  rawValue: string | number | boolean | null | DynamicDateValue,
  valueType?: string
): ItemCondition["value"] {
  if (operator === "$eq") {
    if (valueType === "number" && typeof rawValue === "string") {
      return Number(rawValue) as ItemCondition["value"];
    }
    return rawValue as ItemCondition["value"];
  }
  if (operator === "$exists") return { $exists: rawValue === true || rawValue === "true" };
  if (operator === "$in" || operator === "$nin") {
    const values =
      typeof rawValue === "string"
        ? rawValue
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [rawValue as string | number | boolean | null];
    return operator === "$in" ? { $in: values } : { $nin: values };
  }
  if (["$gt", "$gte", "$lt", "$lte"].includes(operator)) {
    if (valueType === "date") {
      const dateValue = isDynamicDateValue(rawValue) ? rawValue : createDefaultRelativeDateValue();
      switch (operator) {
        case "$gt":
          return { $gt: dateValue };
        case "$gte":
          return { $gte: dateValue };
        case "$lt":
          return { $lt: dateValue };
        case "$lte":
          return { $lte: dateValue };
      }
    }
    const numValue = valueType === "number" ? Number(rawValue) : rawValue;
    switch (operator) {
      case "$gt":
        return { $gt: numValue as number };
      case "$gte":
        return { $gte: numValue as number };
      case "$lt":
        return { $lt: numValue as number };
      case "$lte":
        return { $lte: numValue as number };
    }
  }
  switch (operator) {
    case "$ne":
      return {
        $ne:
          valueType === "number" && typeof rawValue === "string"
            ? Number(rawValue)
            : (rawValue as string | number | boolean | null),
      };
    case "$regex":
      return { $regex: rawValue as string };
  }
  return rawValue as ItemCondition["value"];
}

interface ElemMatchRuleProps {
  condition: ItemCondition;
  source: DataSourceType;
  onChange: (updates: Partial<ItemCondition>) => void;
  onRemove: () => void;
  className?: string;
}

export const ElemMatchRule = ({ condition, source, onChange, onRemove, className }: ElemMatchRuleProps) => {
  const { t } = useTranslation();

  const fields = useMemo(() => getFieldsForSource(source), [source]);
  const fieldDef = getFieldDefinition(source, condition.field);
  const baseFieldId = getBaseFieldId(condition.field);
  const suffixKey = getSuffixKey(condition.field);
  const displayFieldId = fieldDef?.suffixKeyInput ? baseFieldId : condition.field;
  const operators = useMemo(() => getOperatorsForField(fieldDef), [fieldDef]);
  const currentOperator = getOperatorFromValue(condition.value);
  const currentValue = getValueFromCondition(condition.value);
  const operatorDef = operators.find((op) => op.id === currentOperator);

  const handleFieldChange = useCallback(
    (newField: string) => {
      const newFieldDef = getFieldDefinition(source, newField);
      const newOperators = getOperatorsForField(newFieldDef);
      const firstOp = newOperators[0];
      const defaultValue = newFieldDef?.options?.[0]?.value ?? "";
      const newValue = firstOp ? createValueWithOperator(firstOp.id, defaultValue, firstOp.valueType) : "";
      onChange({ field: newField, value: newValue });
    },
    [source, onChange]
  );

  const handleSuffixKeyChange = useCallback(
    (newSuffixKey: string) => {
      const cleanKey = newSuffixKey.replace(/\./g, "");
      onChange({ field: createFieldIdWithSuffix(baseFieldId, cleanKey) });
    },
    [baseFieldId, onChange]
  );

  const handleOperatorChange = useCallback(
    (newOperator: string) => {
      const opDef = operators.find((op) => op.id === newOperator);
      onChange({ value: createValueWithOperator(newOperator, currentValue as string, opDef?.valueType) });
    },
    [operators, currentValue, onChange]
  );

  const handleValueChange = useCallback(
    (newRawValue: string) => {
      onChange({ value: createValueWithOperator(currentOperator, newRawValue, operatorDef?.valueType) });
    },
    [currentOperator, operatorDef?.valueType, onChange]
  );

  const handleBooleanChange = useCallback(
    (boolValue: string) => {
      onChange({ value: createValueWithOperator(currentOperator, boolValue === "true", operatorDef?.valueType) });
    },
    [currentOperator, operatorDef?.valueType, onChange]
  );

  const handleDateValueChange = useCallback(
    (dateValue: DynamicDateValue | number) => {
      onChange({ value: createValueWithOperator(currentOperator, dateValue, "date") });
    },
    [currentOperator, onChange]
  );

  const handleMultiValueChange = useCallback(
    (values: string[]) => {
      const coerced = fieldDef?.type === "number" ? values.map(Number) : values;
      const newValue = currentOperator === "$in" ? { $in: coerced } : { $nin: coerced };
      onChange({ value: newValue });
    },
    [currentOperator, fieldDef?.type, onChange]
  );

  const currentMultiValues = useMemo((): string[] => {
    if (typeof condition.value === "object" && condition.value !== null) {
      if ("$in" in condition.value) return condition.value.$in.map(String);
      if ("$nin" in condition.value) return condition.value.$nin.map(String);
    }
    return [];
  }, [condition.value]);

  return (
    <div className={cn("flex items-center gap-2 p-2 bg-muted/30 rounded-md", className)}>
      <Select value={displayFieldId} onValueChange={handleFieldChange}>
        <SelectTrigger className="w-[160px] h-8 text-xs">
          <SelectValue placeholder={t("Select field")} />
        </SelectTrigger>
        <SelectContent>
          {fields.map((field) => (
            <SelectItem key={field.id} value={field.id} className="text-xs">
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {fieldDef?.suffixKeyInput && (
        <Input
          type="text"
          value={suffixKey}
          onChange={(e) => handleSuffixKeyChange(e.target.value)}
          placeholder={fieldDef.suffixKeyInput.placeholder}
          className="w-[140px] h-8 text-xs"
        />
      )}

      <Select value={currentOperator} onValueChange={handleOperatorChange}>
        <SelectTrigger className="w-[140px] h-8 text-xs">
          <SelectValue placeholder={t("Operator")} />
        </SelectTrigger>
        <SelectContent>
          {operators.map((op) => (
            <SelectItem key={op.id} value={op.id} className="text-xs">
              {op.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {fieldDef?.options ? (
        <Select value={String(currentValue ?? "")} onValueChange={handleValueChange}>
          <SelectTrigger className="flex-1 h-8 text-xs min-w-[120px]">
            <SelectValue placeholder={t("Select value")} />
          </SelectTrigger>
          <SelectContent>
            {fieldDef.options.map((option) => (
              <SelectItem key={option.value} value={option.value} className="text-xs">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : operatorDef?.valueType === "boolean" ? (
        <Select value={String(currentValue)} onValueChange={handleBooleanChange}>
          <SelectTrigger className="w-[100px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true" className="text-xs">
              {t("Yes")}
            </SelectItem>
            <SelectItem value="false" className="text-xs">
              {t("No")}
            </SelectItem>
          </SelectContent>
        </Select>
      ) : operatorDef?.valueType === "date" ? (
        <RelativeDateInput
          value={extractDateValue(condition.value)}
          onChange={handleDateValueChange}
          className="flex-1 min-w-[200px]"
        />
      ) : operatorDef?.valueType === "multiselect" ? (
        <MultiSelect
          options={currentMultiValues.map((v) => ({ value: v, label: v }))}
          value={currentMultiValues}
          onValueChange={handleMultiValueChange}
          placeholder={t("Add values...")}
          className="flex-1 min-w-0 max-h-16 overflow-y-auto text-xs"
          maxDisplayCount={1}
          creator={{
            render: (inputValue) => (
              <span className="text-xs">
                {t("Add")} &quot;{inputValue}&quot;
              </span>
            ),
            transform: (inputValue) => inputValue.trim(),
            onCreate: () => {},
          }}
        />
      ) : (
        <Input
          type={operatorDef?.valueType === "number" ? "number" : "text"}
          value={String(currentValue ?? "")}
          onChange={(e) => handleValueChange(e.target.value)}
          placeholder={t("Value")}
          className="flex-1 h-8 text-xs min-w-[120px]"
        />
      )}

      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};
